import { useEffect, useRef } from 'react';
import { subscribeDeckEvents } from '../api/deck-events-shared';

/**
 * Persistent SSE connection for deck-level events.
 *
 * CF rebuild adapter notes:
 * - The PP/arx contract emitted typed events on a single un-named
 *   SSE channel: agent_status, phase_changed, context_updated,
 *   task_status, etc. Many of those originated in arx (the Tulpa
 *   bridge) which is gone in the CF rebuild.
 * - CF emits NAMED events on /events: `message`, `activity`,
 *   `turn`. We translate them into the PP handler shape so the
 *   downstream UI (PreviewPanel overlays etc.) keeps working.
 *
 * Translation:
 *   activity (kind=agent_thinking) → onTaskStatus('preview:update')
 *   activity (kind=turn_ended)     → onTaskStatus('preview:update:done')
 *
 * The other PP handlers (onPhaseChanged, onAgentStatus,
 * onArtifactSynced, onContextUpdated, onDeckUpdated,
 * onSlideProgress) stay defined for API compatibility but never
 * fire — they reflect arx-specific events we don't emit.
 */

export interface DeckEvent {
  type: string;
  [key: string]: unknown;
}

export interface DeckEventHandlers {
  onAgentStatus?: (status: string, tulpaName?: string) => void;
  onAgentProgramStatus?: (
    programStatus: string,
    tulpaName?: string,
  ) => void;
  onArtifactSynced?: (
    phase: string,
    artifactKey: string,
    data: unknown,
  ) => void;
  onPhaseChanged?: (from: string, to: string) => void;
  onContextUpdated?: (action: string, key: string, actor: string) => void;
  onDeckUpdated?: (fields: string[]) => void;
  onTaskStatus?: (task: string) => void;
  /** artifact_updated / version_created activities (engine builds). */
  onArtifactEvent?: (kind: string, payload: Record<string, unknown>) => void;
  onSlideProgress?: (
    slide: number,
    total: number,
    deckFolder: string,
  ) => void;
}

interface CfActivityEvent {
  kind: string;
  payload: {
    message_id?: string;
    delta?: string;
    detail?: string;
    message?: string;
  };
}

const MAX_RETRY_DELAY_MS = 10_000;

export function useDeckEvents(
  deckId: string | null,
  handlers: DeckEventHandlers,
) {
  const currentDeckRef = useRef(deckId);
  currentDeckRef.current = deckId;
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    if (!deckId) return;

    const sub = subscribeDeckEvents(deckId);
    let active = true;

    // Context-file mutation signal — fires whenever the worker
    // context routes write/delete/toggle a file.
    sub.on('context', (e) => {
        if (!active || currentDeckRef.current !== deckId) return;
        try {
          const ev = JSON.parse(e.data) as {
            kind?: string;
            action?: string;
            key?: string;
            actor?: string;
          };
          if (ev?.kind === 'context_updated' && typeof ev.key === 'string') {
            handlersRef.current.onContextUpdated?.(
              ev.action ?? 'save',
              ev.key,
              ev.actor ?? 'user',
            );
          }
        } catch {
          /* ignore malformed frames */
        }
    });

    // CF named events; translate to PP handler semantics.
    sub.on('activity', (e) => {
        if (!active || currentDeckRef.current !== deckId) return;
        try {
          const a = JSON.parse(e.data) as CfActivityEvent;
          const h = handlersRef.current;
          if (a.kind === 'agent_thinking') {
            h.onTaskStatus?.('preview:update');
          } else if (a.kind === 'turn_ended') {
            h.onTaskStatus?.('preview:update:done');
          } else if (
            a.kind === 'presentation_created' ||
            a.kind === 'presentation_active_changed'
          ) {
            // Trigger a re-fetch of /versions in the consumer.
            h.onTaskStatus?.(`presentation:${a.kind}`);
          } else if (a.kind === 'user_edit') {
            h.onTaskStatus?.('user_edit');
          } else if (a.kind === 'artifact_updated' || a.kind === 'version_created') {
            h.onArtifactEvent?.(a.kind, (a.payload ?? {}) as Record<string, unknown>);
            // Keep the preview fresh on artifact writes too (the SSE
            // event-name-contract lesson: never key refresh off dead kinds).
            h.onTaskStatus?.('preview:update:done');
          }
        } catch {
          /* ignore */
        }
    });

    // PP-shape unnamed events (default SSE name 'message').
    sub.on('message', (e) => {
        if (!active || currentDeckRef.current !== deckId) return;
        try {
          const event: DeckEvent = JSON.parse(e.data);
          const h = handlersRef.current;
          switch (event.type) {
            case 'agent_status':
              h.onAgentStatus?.(
                event.status as string,
                event.tulpa_name as string | undefined,
              );
              break;
            case 'phase_changed':
              h.onPhaseChanged?.(
                event.from as string,
                event.to as string,
              );
              break;
            case 'context_updated':
              h.onContextUpdated?.(
                event.action as string,
                event.key as string,
                event.actor as string,
              );
              break;
            case 'deck_updated':
              h.onDeckUpdated?.(event.fields as string[]);
              break;
            case 'task_status':
              h.onTaskStatus?.(event.task as string);
              break;
            case 'slide_progress':
              h.onSlideProgress?.(
                event.slide as number,
                event.total as number,
                event.deck_folder as string,
              );
              break;
          }
        } catch {
          /* ignore */
        }
    });

    return () => {
      active = false;
      sub.close();
    };
  }, [deckId]);
}
